import React, { CSSProperties, useState, useCallback, useRef } from 'react';
import { Table, Header, flexRender } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { SkeletonLoading } from '@saleshandy/design-system';

import { SortOrder } from '../../../../enums/order';
import Checkbox from '../../../components/atoms/checkbox';
import { OverlayTooltip, Placement } from '../../../components/overlay';
import ColumnHeaderDropdown from './column-header-dropdown';

type IProps = {
  table: Table<any>;
  headerIndex: number;
  header: Header<any, any>;
  sort: {
    sortBy: string;
    order: SortOrder;
  } | null;
  onSort?: ((sortBy: string, order: SortOrder) => void) | null;
  checkboxState: {
    isChecked: boolean;
    isIntermediate: boolean;
    isDisabled?: boolean;
  };
  onRowSelectAllHandler: (isSelect: boolean) => void;
  isLoading?: boolean;
  isPinned?: boolean;
  enableDrag?: boolean;
  isDefaultPinned?: boolean;
  isFrozen?: boolean;
  onFreezeColumn?: ((columnId: string) => void) | null;
  onUnfreezeColumn?: ((columnId: string) => void) | null;
  onMoveLeft?: ((columnId: string) => void) | null;
  onMoveRight?: ((columnId: string) => void) | null;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  isAnyDropdownOpen?: boolean;
  onDropdownOpen?: (columnId: string) => void;
  onDropdownClose?: () => void;
  enableRowSelection?: boolean;
};

const DraggableColumnHeader: React.FC<IProps> = ({
  table,
  headerIndex,
  header,
  sort,
  onSort,
  checkboxState,
  onRowSelectAllHandler,
  isLoading = false,
  enableRowSelection = true,
  isPinned = false,
  enableDrag = false,
  isDefaultPinned = false,
  isFrozen = false,
  onFreezeColumn = null,
  onUnfreezeColumn = null,
  onMoveLeft = null,
  onMoveRight = null,
  canMoveLeft = true,
  canMoveRight = true,
  isAnyDropdownOpen = false,
  onDropdownOpen,
  onDropdownClose,
}) => {
  const { column } = header;
  const columnId = header.id;
  const isFirstColumn = headerIndex === 0;

  // State for dropdown (local state)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Track if we've moved during pointer down (to distinguish click from drag)
  const hasMoved = useRef(false);
  const pointerStartPos = useRef<{ x: number; y: number } | null>(null);

  // Ref for dropdown container to check if clicks are inside it
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  // Only enable sorting for non-pinned columns when enableDrag is true
  // Frozen columns also cannot be dragged
  // Also disable drag when ANY dropdown is open
  const canDrag =
    enableDrag &&
    !isPinned &&
    !isFrozen &&
    !isFirstColumn &&
    !isAnyDropdownOpen;

  // Determine if dropdown should be shown (not for first column and not for default pinned)
  const showDropdown = !isFirstColumn && !isDefaultPinned;

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't open dropdown if dragging or if it's the first column
      if (isFirstColumn || isDefaultPinned) return;

      e.stopPropagation();
      setIsDropdownOpen((prev) => {
        const newState = !prev;
        if (newState) {
          onDropdownOpen?.(columnId);
        } else {
          onDropdownClose?.();
        }
        return newState;
      });
    },
    [isFirstColumn, isDefaultPinned, columnId, onDropdownOpen, onDropdownClose],
  );

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    onDropdownClose?.();
  }, [onDropdownClose]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: columnId,
    disabled: !canDrag,
  });

  const getSortState = () => {
    if (!sort) return null;

    const columnMeta = column.columnDef.meta as any;
    const sortBy = columnMeta?.sortBy;

    if (sortBy === sort.sortBy) {
      return sort.order;
    }
    return null;
  };

  const handleSort = () => {
    if (!onSort) return;

    const columnMeta = column.columnDef.meta as any;
    const sortBy = columnMeta?.sortBy;

    if (!sortBy) return;

    const currentSortState = getSortState();
    let newOrder: SortOrder;

    if (currentSortState === null) {
      newOrder = SortOrder.ASC;
    } else if (currentSortState === SortOrder.ASC) {
      newOrder = SortOrder.DESC;
    } else {
      newOrder = SortOrder.ASC;
    }

    onSort(columnId, newOrder);
  };

  const getColumnHeaderStyles = (): CSSProperties => {
    const isPinnedColumn = header.column.getIsPinned();
    const columnDefSize = (header.column.columnDef.size as number) || 0;
    const currentSize = header.getSize();
    const finalSize = currentSize > 0 ? currentSize : columnDefSize || 50;

    const baseStyles: CSSProperties = {
      width: `calc(var(--header-${columnId}-size, ${finalSize}) * 1px)`,
      minWidth: `${finalSize}px`,
      left:
        isPinnedColumn === 'left'
          ? `${header.column.getStart('left')}px`
          : undefined,
      right:
        isPinnedColumn === 'right'
          ? `${header.column.getAfter('right')}px`
          : undefined,
    };

    // Apply drag transform styles - only use translate, not scale to preserve size
    if (canDrag && transform) {
      // Use only translate values, ignore scale to prevent size changes
      const translateOnly = {
        ...transform,
        scaleX: 1,
        scaleY: 1,
      };

      return {
        ...baseStyles,
        transform: CSS.Transform.toString(translateOnly),
        transition,
        zIndex: isDragging ? 10 : undefined,
      };
    }

    if (canDrag) {
      return {
        ...baseStyles,
        transition,
      };
    }

    return baseStyles;
  };

  const getColumnHeaderClassNames = () => {
    const isPinnedColumn = header.column.getIsPinned();
    const isLastPinnedLeft =
      isPinnedColumn === 'left' && header.column.getIsLastColumn('left');
    const columnMeta = header.column.columnDef.meta as
      | Record<string, unknown>
      | undefined;
    const headerClassName = columnMeta?.headerClassName as string | undefined;

    return classNames(
      'tanstack-table--th',
      {
        'th--pinned': isPinnedColumn,
        'th--pinned__left': isLastPinnedLeft,
        'th--draggable': canDrag,
        'th--dragging': isDragging,
        'th--frozen': isFrozen,
        'th--dropdown-open': isDropdownOpen,
        'th--has-dropdown': showDropdown,
      },
      headerClassName,
    );
  };

  const getColumnHeaderResizerClassNames = () => {
    const isResizing = header.column.getIsResizing();

    return classNames(
      'column-header-resizer',
      table.options.columnResizeDirection,
      {
        'column-header-resizer-active': isResizing,
        isResizing,
      },
    );
  };

  const sortState = getSortState();
  const columnMeta = column.columnDef.meta as any;
  const isSortable = !!columnMeta?.sortBy && !!onSort;

  // Get header skeleton from column definition meta or columnDef
  const getHeaderSkeleton = () => {
    const skeleton =
      columnMeta?.headerSkeleton || (column.columnDef as any).headerSkeleton;

    if (skeleton) {
      return skeleton;
    }

    // Default skeleton if not provided
    return <SkeletonLoading width={100} height={20} />;
  };

  // Drag handle props - only apply when dragging is enabled AND dropdown is closed
  // When dropdown is open, we disable dnd completely to allow simple click handling
  const shouldEnableDrag = canDrag && !isDropdownOpen;
  const dragHandleProps = shouldEnableDrag
    ? { ...attributes, ...listeners }
    : {};

  // Handle pointer down - track start position (only when not dropdown open)
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isDropdownOpen) return; // Skip tracking when dropdown is open
      hasMoved.current = false;
      pointerStartPos.current = { x: e.clientX, y: e.clientY };
    },
    [isDropdownOpen],
  );

  // Handle pointer move - check if we've moved significantly
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isDropdownOpen) return; // Skip tracking when dropdown is open
      if (pointerStartPos.current) {
        const dx = Math.abs(e.clientX - pointerStartPos.current.x);
        const dy = Math.abs(e.clientY - pointerStartPos.current.y);
        if (dx > 3 || dy > 3) {
          hasMoved.current = true;
        }
      }
    },
    [isDropdownOpen],
  );

  // Handle pointer up - treat as click if we haven't moved
  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (isFirstColumn || isLoading) return;

      // When dropdown is open, check if click is inside the dropdown
      if (isDropdownOpen) {
        // Check if click target is inside the dropdown - if so, let dropdown handle it
        if (dropdownContainerRef.current?.contains(e.target as Node)) {
          return; // Let the dropdown handle its own clicks
        }

        // Click was outside dropdown (on header), close the dropdown
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setIsDropdownOpen(false);
        onDropdownClose?.();
        pointerStartPos.current = null;
        hasMoved.current = false;
        return;
      }

      const startPos = pointerStartPos.current;
      pointerStartPos.current = null;

      // If we moved, this was a drag - don't handle as click
      if (hasMoved.current) {
        hasMoved.current = false;
        return;
      }

      // Check final position as well
      if (startPos) {
        const dx = Math.abs(e.clientX - startPos.x);
        const dy = Math.abs(e.clientY - startPos.y);
        if (dx > 3 || dy > 3) {
          return;
        }
      }

      // This is a click - handle it
      e.stopPropagation();

      if (showDropdown) {
        // Open the dropdown
        setIsDropdownOpen(true);
        onDropdownOpen?.(columnId);
      } else if (isSortable) {
        handleSort();
      }
    },
    [
      isFirstColumn,
      isLoading,
      isDropdownOpen,
      showDropdown,
      columnId,
      onDropdownOpen,
      onDropdownClose,
      isSortable,
      handleSort,
    ],
  );

  // Handle mouse down - stop native event propagation when dropdown is open
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isDropdownOpen && showDropdown) {
        // Stop both React and native event propagation
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }
    },
    [isDropdownOpen, showDropdown],
  );

  return (
    <div
      ref={shouldEnableDrag ? setNodeRef : undefined}
      className={getColumnHeaderClassNames()}
      style={getColumnHeaderStyles()}
      {...dragHandleProps}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="column-header-content"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onMouseDown={handleMouseDown}
        onKeyDown={(e) => {
          if (isFirstColumn || isLoading || isDragging) return;
          if (e.key === 'Enter') {
            if (showDropdown) {
              handleHeaderClick(e as any);
            } else if (isSortable) {
              handleSort();
            }
          }
        }}
        role={
          !isFirstColumn && (showDropdown || isSortable) ? 'button' : undefined
        }
        tabIndex={
          !isFirstColumn && (showDropdown || isSortable) ? 0 : undefined
        }
        style={{
          cursor:
            !isFirstColumn &&
            (showDropdown || (isSortable && !isLoading && !isDragging))
              ? 'pointer'
              : undefined,
        }}
      >
        <div className="column-header-content--name">
          {isFirstColumn ? (
            <>
              {enableRowSelection && !isLoading && (
                <div className="column-header-content--checkbox">
                  <Checkbox
                    checked={checkboxState.isChecked}
                    intermediate={checkboxState.isIntermediate}
                    onChange={() =>
                      onRowSelectAllHandler(!checkboxState.isChecked)
                    }
                    disabled={checkboxState.isDisabled || isLoading}
                    className={
                      checkboxState.isDisabled ? 'show-disable-style' : ''
                    }
                  />
                </div>
              )}
              <span className="column-header-content--headText">
                <span className="column-header-content--colHeadText">
                  {isLoading
                    ? getHeaderSkeleton()
                    : flexRender(column.columnDef.header, header.getContext())}
                </span>
              </span>
            </>
          ) : (
            <div className="column-header-content--headText">
              <span className="column-header-content--colHeadText">
                {isLoading
                  ? getHeaderSkeleton()
                  : flexRender(column.columnDef.header, header.getContext())}
              </span>
              {isSortable && !isLoading && (
                <span className="column-header-content--sorting-icons">
                  {sortState === SortOrder.ASC && '↑'}
                  {sortState === SortOrder.DESC && '↓'}
                  {sortState === null && '↕'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Column Header Dropdown */}
        {showDropdown && !isLoading && (
          <div ref={dropdownContainerRef}>
            <ColumnHeaderDropdown
              isOpen={isDropdownOpen}
              onClose={handleCloseDropdown}
              columnId={columnId}
              isFrozen={isFrozen}
              isDefaultPinned={isDefaultPinned}
              onFreezeColumn={onFreezeColumn}
              onUnfreezeColumn={onUnfreezeColumn}
              onMoveLeft={onMoveLeft}
              onMoveRight={onMoveRight}
              canMoveLeft={canMoveLeft}
              canMoveRight={canMoveRight}
            />
          </div>
        )}
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={getColumnHeaderResizerClassNames()}
        onDoubleClick={() => header.column.resetSize()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        onPointerDown={(e) => e.stopPropagation()}
        role="separator"
        aria-label={`Resize ${column.columnDef.header}`}
      >
        <div className="column-header-resizer--handle" />
      </div>
    </div>
  );
};

export default DraggableColumnHeader;
